import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@byscott-io/corebyscott';
import { Link } from 'react-router-dom';
import { affiliates } from '../api/client';

export default function AffiliatesPage() {
  const [affiliateList, setAffiliateList] = useState([]);
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await affiliates.list(activeOnly);
      setAffiliateList(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load affiliates');
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeactivate = async (affiliate) => {
    if (!confirm(`Deactivate affiliate "${affiliate.name}"? Their referral codes stay but the affiliate is marked inactive.`)) return;
    try {
      await affiliates.delete(affiliate.id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to deactivate affiliate');
    }
  };

  const handleApprove = async (affiliate) => {
    if (!confirm(`Approve "${affiliate.name}"? They'll be emailed a sign-in link to the affiliate portal.`)) return;
    try {
      await affiliates.update(affiliate.id, { active: true });
      loadData();
    } catch (err) {
      alert(err.response?.data?.errors?.join(', ') || err.response?.data?.error || 'Failed to approve affiliate');
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading...</div>;
  if (error) return <div className="p-6"><div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div></div>;

  const pendingCount = affiliateList.filter((a) => a.pending).length;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Affiliates</h1>
          {pendingCount > 0 && (
            <span className="px-2 py-0.5 text-sm rounded-full bg-amber-100 text-amber-800 font-medium">
              {pendingCount} pending application{pendingCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <button onClick={() => setShowCreateModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
          + New Affiliate
        </button>
      </div>

      <p className="text-gray-600 mb-6">
        Affiliates are ambassadors who market your apps. Pending applications appear first. Drill into one to manage the apps they market and their commission codes.
      </p>

      <label className="flex items-center mb-4 text-sm text-gray-700">
        <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
        <span className="ml-2">Active only</span>
      </label>

      {affiliateList.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <p className="text-gray-500 mb-4">No affiliates yet.</p>
          <button onClick={() => setShowCreateModal(true)} className="text-blue-600 hover:text-blue-800">Create an affiliate</button>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
          {affiliateList.map((affiliate) => (
            <div key={affiliate.id} className="p-4 flex justify-between items-center">
              <div>
                <Link to={`/app/affiliates/${affiliate.id}`} className="font-medium text-blue-600 hover:text-blue-800">{affiliate.name}</Link>
                {affiliate.pending
                  ? <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800">Pending</span>
                  : (!affiliate.active && <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Inactive</span>)}
                {affiliate.email && <p className="text-sm text-gray-500">{affiliate.email}</p>}
              </div>
              <div className="flex gap-3 items-center">
                {affiliate.pending && <button onClick={() => handleApprove(affiliate)} className="text-green-600 hover:text-green-800 text-sm font-medium">Approve</button>}
                <Link to={`/app/affiliates/${affiliate.id}`} className="text-blue-600 hover:text-blue-800 text-sm">Apps &amp; codes →</Link>
                <button onClick={() => setEditingAffiliate(affiliate)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                {affiliate.active && <button onClick={() => handleDeactivate(affiliate)} className="text-red-600 hover:text-red-800 text-sm">Deactivate</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <AffiliateModal onClose={() => setShowCreateModal(false)} onSaved={() => { setShowCreateModal(false); loadData(); }} />
      )}

      {editingAffiliate && (
        <AffiliateModal affiliate={editingAffiliate} onClose={() => setEditingAffiliate(null)} onSaved={() => { setEditingAffiliate(null); loadData(); }} />
      )}
    </div>
  );
}

export function AffiliateModal({ affiliate, onClose, onSaved }) {
  const [name, setName] = useState(affiliate?.name || '');
  const [email, setEmail] = useState(affiliate?.email || '');
  const [active, setActive] = useState(affiliate ? affiliate.active : true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const payload = { name, email, active };
      if (affiliate) {
        await affiliates.update(affiliate.id, payload);
      } else {
        await affiliates.create(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.errors?.join(', ') || err.response?.data?.error || 'Failed to save affiliate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={affiliate ? 'Edit Affiliate' : 'New Affiliate'} size="md">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g., Jane Smith" required />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="jane@example.com" />
        </div>
        <div className="mb-6">
          <label className="flex items-center">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <span className="ml-2 text-sm text-gray-700">Active</span>
          </label>
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button type="submit" disabled={saving || !name} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : (affiliate ? 'Save' : 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
